import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { CartProvider } from "@/contexts/CartContext";
import { SettingsProvider } from "@/contexts/SettingsContext";

import Layout from "@/components/Layout";
import ProtectedRoute from "@/components/ProtectedRoute";

import Home from "@/pages/Home";
import Shop from "@/pages/Shop";
import ProductDetail from "@/pages/ProductDetail";
import Cart from "@/pages/Cart";
import Checkout from "@/pages/Checkout";
import AIDoctor from "@/pages/AIDoctor";
import Doctors from "@/pages/Doctors";
import DoctorEnroll from "@/pages/DoctorEnroll";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import MyOrders from "@/pages/MyOrders";
import MyAppointments from "@/pages/MyAppointments";
import DoctorDashboard from "@/pages/DoctorDashboard";
import SearchResults from "@/pages/SearchResults";
import PortalLogin from "@/pages/PortalLogin";
import DistributorRegister from "@/pages/DistributorRegister";
import InvoicePrint from "@/pages/InvoicePrint";

import DistributorLayout from "@/pages/distributor/DistributorLayout";
import DistributorDashboard from "@/pages/distributor/DistributorDashboard";
import DistributorShop from "@/pages/distributor/DistributorShop";
import DistributorOrders from "@/pages/distributor/DistributorOrders";
import DistributorInvoices from "@/pages/distributor/DistributorInvoices";
import DistributorStatement from "@/pages/distributor/DistributorStatement";
import DistributorPriceList from "@/pages/distributor/DistributorPriceList";

import EmployeeLayout from "@/pages/employee/EmployeeLayout";
import EmployeeHub from "@/pages/employee/EmployeeHub";
import MRPlaceOrder from "@/pages/employee/MRPlaceOrder";
import MRDRS from "@/pages/employee/MRDRS";
import MRDues from "@/pages/employee/MRDues";
import PackingPortal from "@/pages/employee/PackingPortal";
import AccountsInvoices from "@/pages/employee/AccountsInvoices";
import AccountsInvoiceEditor from "@/pages/employee/AccountsInvoiceEditor";
import AccountsCustomers from "@/pages/employee/AccountsCustomers";
import AccountsDues from "@/pages/employee/AccountsDues";

import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminCategories from "@/pages/admin/AdminCategories";
import AdminSizes from "@/pages/admin/AdminSizes";
import AdminProducts from "@/pages/admin/AdminProducts";
import AdminDoctorsRegistered from "@/pages/admin/AdminDoctorsRegistered";
import AdminDoctorsInternet from "@/pages/admin/AdminDoctorsInternet";
import AdminOrders from "@/pages/admin/AdminOrders";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminDesignations from "@/pages/admin/AdminDesignations";
import AdminEmployees from "@/pages/admin/AdminEmployees";
import AdminDistributors from "@/pages/admin/AdminDistributors";
import AdminDistributorPricing from "@/pages/admin/AdminDistributorPricing";
import AdminDRSFields from "@/pages/admin/AdminDRSFields";
import AdminInvoiceTemplates from "@/pages/admin/AdminInvoiceTemplates";
import AdminEditors from "@/pages/admin/AdminEditors";
import AdminActivityLog from "@/pages/admin/AdminActivityLog";
import AdminBulkProducts from "@/pages/admin/AdminBulkProducts";
import AdminPhotoCoverage from "@/pages/admin/AdminPhotoCoverage";
import AdminFeatureFlags from "@/pages/admin/AdminFeatureFlags";
import AdminStock from "@/pages/admin/AdminStock";
import StockPortal from "@/pages/employee/StockPortal";
import AccountsExternalInvoices from "@/pages/employee/AccountsExternalInvoices";
import MRMarketExpenses from "@/pages/employee/MRMarketExpenses";
import AdminMRExpenses from "@/pages/admin/AdminMRExpenses";
import AccountsDayBook from "@/pages/employee/AccountsDayBook";
import AccountsSalesRegister from "@/pages/employee/AccountsSalesRegister";
import AccountsOutstanding from "@/pages/employee/AccountsOutstanding";
import AccountsStockSummary from "@/pages/employee/AccountsStockSummary";
import AccountsGSTReturns from "@/pages/employee/AccountsGSTReturns";
import AccountsJournal from "@/pages/employee/AccountsJournal";
import AccountsProfitLoss from "@/pages/employee/AccountsProfitLoss";
import AccountsBalanceSheet from "@/pages/employee/AccountsBalanceSheet";
import { TermsPage, PrivacyPage, RefundPage, ShippingPage } from "@/pages/LegalPages";
import StaffRegister from "@/pages/StaffRegister";
import AccountDelete from "@/pages/AccountDelete";
import ChangePassword from "@/pages/ChangePassword";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import OrderStatus from "@/pages/OrderStatus";
import OrderDetailPage from "@/pages/OrderDetailPage";
import CRM from "@/pages/CRM";
import CRMContactDetail from "@/pages/CRMContactDetail";
import PackingSlip from "@/pages/PackingSlip";
import PackingSlips from "@/pages/PackingSlips";
import AdminDRSAnalytics from "@/pages/admin/AdminDRSAnalytics";
import AdminTraffic from "@/pages/admin/AdminTraffic";
import AdminSEO from "@/pages/admin/AdminSEO";
import AdminGEO from "@/pages/admin/AdminGEO";
import AdminSalesAnalytics from "@/pages/admin/AdminSalesAnalytics";
import DistributorHub from "@/pages/admin/DistributorHub";
import AdminLegalPages from "@/pages/admin/AdminLegalPages";
import AdminAppointments from "@/pages/admin/AdminAppointments";
import AdminReviews from "@/pages/admin/AdminReviews";
import AdminBlog from "@/pages/admin/AdminBlog";
import AdminMarketing from "@/pages/admin/AdminMarketing";
import AdminSubscriptions from "@/pages/admin/AdminSubscriptions";
import AdminSubAnalytics from "@/pages/admin/AdminSubAnalytics";
import AdminNotifications from "@/pages/admin/AdminNotifications";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import VideoCall from "@/pages/VideoCall";
import OrderTracking from "@/pages/OrderTracking";
import MySubscriptions from "@/pages/MySubscriptions";
import PayNow from "@/pages/PayNow";
import RecoverCart from "@/pages/RecoverCart";

function App() {
    return (
        <div className="App">
            <AuthProvider>
                <CartProvider>
                    <BrowserRouter>
                        <SettingsProvider>
                        <Routes>
                            <Route path="/" element={<Layout><Home /></Layout>} />
                            <Route path="/shop" element={<Layout><Shop /></Layout>} />
                            <Route path="/search" element={<Layout><SearchResults /></Layout>} />
                            <Route path="/product/:slug" element={<Layout><ProductDetail /></Layout>} />
                            <Route path="/cart" element={<Layout><Cart /></Layout>} />
                            <Route path="/checkout" element={<ProtectedRoute><Layout><Checkout /></Layout></ProtectedRoute>} />
                            <Route path="/ai-doctor" element={<Layout><AIDoctor /></Layout>} />
                            <Route path="/doctors" element={<Layout><Doctors /></Layout>} />
                            <Route path="/enroll-doctor" element={<Layout><DoctorEnroll /></Layout>} />
                            <Route path="/login" element={<Layout><PortalLogin /></Layout>} />
                            <Route path="/login-customer" element={<Layout><Login /></Layout>} />
                            <Route path="/terms" element={<Layout><TermsPage /></Layout>} />
                            <Route path="/privacy" element={<Layout><PrivacyPage /></Layout>} />
                            <Route path="/refunds" element={<Layout><RefundPage /></Layout>} />
                            <Route path="/shipping" element={<Layout><ShippingPage /></Layout>} />
                            <Route path="/account/delete" element={<Layout><AccountDelete /></Layout>} />
                            <Route path="/account/change-password" element={<ProtectedRoute><Layout><ChangePassword /></Layout></ProtectedRoute>} />
                            <Route path="/forgot-password" element={<Layout><ForgotPassword /></Layout>} />
                            <Route path="/reset-password/:token" element={<Layout><ResetPassword /></Layout>} />
                            <Route path="/register" element={<Layout><Register /></Layout>} />
                            <Route path="/staff-register" element={<Layout><StaffRegister /></Layout>} />
                            <Route path="/distributor/register" element={<Layout><DistributorRegister /></Layout>} />
                            <Route path="/orders" element={<ProtectedRoute><Layout><MyOrders /></Layout></ProtectedRoute>} />
                            <Route path="/my-appointments" element={<ProtectedRoute><Layout><MyAppointments /></Layout></ProtectedRoute>} />
                            <Route path="/doctor" element={<ProtectedRoute role="doctor"><Layout><DoctorDashboard /></Layout></ProtectedRoute>} />
                            <Route path="/blog" element={<Layout><Blog /></Layout>} />
                            <Route path="/blog/:slug" element={<Layout><BlogPost /></Layout>} />
                            <Route path="/video-call/:id" element={<ProtectedRoute><VideoCall /></ProtectedRoute>} />
                            <Route path="/track" element={<Layout><OrderTracking /></Layout>} />
                            <Route path="/pay/:token" element={<Layout><PayNow /></Layout>} />
                            <Route path="/recover-cart/:token" element={<Layout><RecoverCart /></Layout>} />
                            <Route path="/track/:id" element={<Layout><OrderTracking /></Layout>} />
                            <Route path="/subscriptions" element={<ProtectedRoute><Layout><MySubscriptions /></Layout></ProtectedRoute>} />

                            <Route path="/invoice/:id" element={<InvoicePrint />} />

                            <Route path="/distributor" element={<ProtectedRoute role="distributor"><DistributorLayout /></ProtectedRoute>}>
                                <Route index element={<DistributorDashboard />} />
                                <Route path="shop" element={<DistributorShop />} />
                                <Route path="orders" element={<DistributorOrders />} />
                                <Route path="orders/:id" element={<OrderDetailPage />} />
                                <Route path="invoices" element={<DistributorInvoices />} />
                                <Route path="statement" element={<DistributorStatement />} />
                                <Route path="price-list" element={<DistributorPriceList />} />
                            </Route>

                            <Route path="/employee" element={<ProtectedRoute role="employee"><EmployeeLayout /></ProtectedRoute>}>
                                <Route index element={<EmployeeHub />} />
                                <Route path="order-status" element={<OrderStatus />} />
                                <Route path="order-status/:id" element={<OrderDetailPage />} />
                                <Route path="mr/place-order" element={<MRPlaceOrder />} />
                                <Route path="crm" element={<CRM basePath="/employee/crm" />} />
                                <Route path="crm/:id" element={<CRMContactDetail basePath="/employee/crm" />} />
                                <Route path="mr/drs" element={<MRDRS />} />
                                <Route path="mr/dues" element={<MRDues />} />
                                <Route path="mr/expenses" element={<MRMarketExpenses />} />
                                <Route path="distributor-hub" element={<DistributorHub />} />
                                <Route path="distributor-hub/:id" element={<DistributorHub />} />
                                <Route path="packing" element={<PackingPortal />} />
                                <Route path="packing/slip/:id" element={<PackingSlip />} />
                                <Route path="packing-slips" element={<PackingSlips />} />
                                <Route path="packing-slips/:id" element={<PackingSlip />} />
                                <Route path="stock" element={<StockPortal />} />
                                <Route path="accounts/invoices" element={<AccountsInvoices />} />
                                <Route path="accounts/uploads" element={<AccountsExternalInvoices />} />
                                <Route path="accounts/invoices/new" element={<AccountsInvoiceEditor />} />
                                <Route path="accounts/invoices/:id" element={<AccountsInvoiceEditor />} />
                                <Route path="accounts/customers" element={<AccountsCustomers />} />
                                <Route path="accounts/dues" element={<AccountsDues />} />
                                <Route path="accounts/daybook" element={<AccountsDayBook />} />
                                <Route path="accounts/sales-register" element={<AccountsSalesRegister />} />
                                <Route path="accounts/outstanding" element={<AccountsOutstanding />} />
                                <Route path="accounts/stock-summary" element={<AccountsStockSummary />} />
                                <Route path="accounts/gst-returns" element={<AccountsGSTReturns />} />
                                <Route path="accounts/journal" element={<AccountsJournal />} />
                                <Route path="accounts/pnl" element={<AccountsProfitLoss />} />
                                <Route path="accounts/balance-sheet" element={<AccountsBalanceSheet />} />
                            </Route>

                            <Route path="/admin" element={<ProtectedRoute role={["admin", "editor"]}><AdminLayout /></ProtectedRoute>}>
                                <Route index element={<AdminDashboard />} />
                                <Route path="categories" element={<AdminCategories />} />
                                <Route path="sizes" element={<AdminSizes />} />
                                <Route path="products" element={<AdminProducts />} />
                                <Route path="bulk-products" element={<AdminBulkProducts />} />
                                <Route path="photo-coverage" element={<AdminPhotoCoverage />} />
                                <Route path="feature-flags" element={<AdminFeatureFlags />} />
                                <Route path="traffic" element={<AdminTraffic />} />
                                <Route path="seo" element={<AdminSEO />} />
                                <Route path="geo" element={<AdminGEO />} />
                                <Route path="sales-analytics" element={<AdminSalesAnalytics />} />
                                <Route path="stock" element={<AdminStock />} />
                                <Route path="distributor-pricing" element={<AdminDistributorPricing />} />
                                <Route path="doctors-registered" element={<AdminDoctorsRegistered />} />
                                <Route path="doctors-internet" element={<AdminDoctorsInternet />} />
                                <Route path="distributors" element={<AdminDistributors />} />
                                <Route path="distributor-hub" element={<DistributorHub />} />
                                <Route path="distributor-hub/:id" element={<DistributorHub />} />
                                <Route path="employees" element={<AdminEmployees />} />
                                <Route path="designations" element={<AdminDesignations />} />
                                <Route path="drs-fields" element={<AdminDRSFields />} />
                                <Route path="invoice-templates" element={<AdminInvoiceTemplates />} />
                                <Route path="orders" element={<AdminOrders />} />
                                <Route path="order-status" element={<OrderStatus />} />
                                <Route path="order-status/:id" element={<OrderDetailPage />} />
                                <Route path="packing-slips" element={<PackingSlips />} />
                                <Route path="packing-slips/:id" element={<PackingSlip />} />
                                <Route path="crm" element={<CRM basePath="/admin/crm" />} />
                                <Route path="crm/:id" element={<CRMContactDetail basePath="/admin/crm" />} />
                                <Route path="users" element={<AdminUsers />} />
                                <Route path="editors" element={<ProtectedRoute role="admin"><AdminEditors /></ProtectedRoute>} />
                                <Route path="activity" element={<AdminActivityLog />} />
                                <Route path="mr-expenses" element={<AdminMRExpenses />} />
                                <Route path="drs-analytics" element={<AdminDRSAnalytics />} />
                                <Route path="reviews" element={<AdminReviews />} />
                                <Route path="blog" element={<AdminBlog />} />
                                <Route path="marketing" element={<AdminMarketing />} />
                                <Route path="subscriptions" element={<AdminSubscriptions />} />
                                <Route path="subscriptions/analytics" element={<AdminSubAnalytics />} />
                                <Route path="notifications" element={<AdminNotifications />} />
                                {/* Billing */}
                                <Route path="invoices" element={<AccountsInvoices />} />
                                <Route path="invoices/new" element={<AccountsInvoiceEditor />} />
                                <Route path="invoices/:id" element={<AccountsInvoiceEditor />} />
                                <Route path="external-invoices" element={<AccountsExternalInvoices />} />
                                <Route path="customers" element={<AccountsCustomers />} />
                                <Route path="dues" element={<AccountsDues />} />
                                {/* Tally-style Hub */}
                                <Route path="daybook" element={<AccountsDayBook />} />
                                <Route path="sales-register" element={<AccountsSalesRegister />} />
                                <Route path="outstanding" element={<AccountsOutstanding />} />
                                <Route path="stock-summary" element={<AccountsStockSummary />} />
                                <Route path="gst-returns" element={<AccountsGSTReturns />} />
                                <Route path="journal" element={<AccountsJournal />} />
                                <Route path="pnl" element={<AccountsProfitLoss />} />
                                <Route path="balance-sheet" element={<AccountsBalanceSheet />} />
                                <Route path="settings" element={<AdminSettings />} />
                                <Route path="legal" element={<AdminLegalPages />} />
                                <Route path="appointments" element={<AdminAppointments />} />
                            </Route>
                        </Routes>
                        </SettingsProvider>
                    </BrowserRouter>
                </CartProvider>
            </AuthProvider>
        </div>
    );
}

export default App;
